import { cn } from "@/lib/utils";
import { SupportDetails } from "@/components/ui/copyable-reference";

/**
 * Table state surface (loading / empty / error) for a data table.
 *
 * Renders as a single full-width row so the column headers stay visible while
 * the table has no rows, and so a table never falls back to a bare div sitting
 * outside the wrapper it belongs to. The error variant is the only one that
 * carries a live region and the D636 support reference: an empty result is not
 * a failure, and must not read like one.
 *
 * Used by the admin Role Catalog; it holds no catalog knowledge, so the next
 * table can adopt it unchanged.
 */
export type TableStateVariant = "loading" | "empty" | "error";

export interface TableStateProps {
  variant: TableStateVariant;
  /** Headline for the state. Always specific to the table it sits in. */
  title: string;
  /** One sentence saying what the state means and what happens next. */
  description?: string;
  /** Columns to span, so the row covers the whole header. */
  colSpan: number;
  /** Error only: re-issues the request that failed. */
  onRetry?: () => void;
  /** Error only: the support reference from the failed response (D636). */
  supportReference?: string | null;
  /** Error only: the capability a 403 body named. */
  capability?: string | null;
  className?: string;
}

export function TableState({
  variant,
  title,
  description,
  colSpan,
  onRetry,
  supportReference,
  capability,
  className,
}: TableStateProps) {
  const isError = variant === "error";
  return (
    <tr className={cn("border-b border-line last:border-b-0", className)}>
      <td colSpan={colSpan} className="p-0">
        <div
          role={isError ? "alert" : variant === "loading" ? "status" : undefined}
          aria-live={variant === "loading" ? "polite" : undefined}
          className={cn(
            "flex flex-col items-start gap-1 px-4 py-8 text-sm",
            isError ? "bg-danger-soft text-danger" : "text-muted",
          )}
        >
          <div className={cn("font-semibold", isError ? "text-danger" : "text-ink")}>{title}</div>
          {description && <p className="max-w-[560px] leading-relaxed">{description}</p>}
          {isError && onRetry && (
            <button type="button" className="underline" onClick={onRetry}>
              Retry
            </button>
          )}
          {isError && <SupportDetails reference={supportReference} capability={capability} />}
        </div>
      </td>
    </tr>
  );
}
